import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Download, Printer } from "lucide-react";
import api from "../../services/api";
import {
  EmptyRow,
  LoadingOverlay,
  MultiSelect,
  RECEIVABLES_API,
  fmtDate,
  fmtDateTime,
  idList,
  money,
  sumBy,
  todayIso,
  useReceivableLookups,
  useReceivableSettings,
} from "./common";
import { printSheet } from "./deliverySheetPrint";

const InvoiceTable = ({ rows, selected, onToggle, onToggleAll, extraColumns = [] }) => {
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.invoiceId));
  return (
    <div className="table-wrapper" style={{ maxHeight: "50vh", overflowY: "auto" }}>
      <table>
        <thead>
          <tr>
            <th>
              <input type="checkbox" checked={allSelected} onChange={() => onToggleAll(!allSelected)} aria-label="Select all" />
            </th>
            <th>Invoice #</th>
            <th>Customer</th>
            <th>Phone</th>
            <th>Beat</th>
            <th>Salesman</th>
            <th>Invoice Date</th>
            <th style={{ textAlign: "right" }}>Invoice Amt</th>
            <th style={{ textAlign: "right" }}>Balance</th>
            {extraColumns.map((c) => (
              <th key={c.label}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow colSpan={9 + extraColumns.length} title="No invoices" />
          ) : (
            rows.map((row) => (
              <tr key={row.invoiceId}>
                <td>
                  <input type="checkbox" checked={selected.has(row.invoiceId)} onChange={() => onToggle(row.invoiceId)} />
                </td>
                <td>{row.invoiceNumber}</td>
                <td title={row.customerName}>{row.customerName}</td>
                <td>{row.customerPhone || <span className="badge badge-red">No phone</span>}</td>
                <td>{row.beatName || "-"}</td>
                <td>{row.salesmanName || "-"}</td>
                <td>{fmtDate(row.invoiceDate)}</td>
                <td style={{ textAlign: "right" }}>{money(row.invoiceAmount)}</td>
                <td style={{ textAlign: "right" }}>{money(row.balanceAmount)}</td>
                {extraColumns.map((c) => (
                  <td key={c.label}>{c.render(row)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const EMPTY_ASSIGNED = { rows: [], otpVisible: false, totalBalance: 0, pendingRows: [], totalPendingBalance: 0 };

/** Older bills with an outstanding balance for the customers of the new (assigned) bills, on those bills' beats. */
const PendingBillsTable = ({ rows }) => (
  <div className="table-wrapper" style={{ maxHeight: "40vh", overflowY: "auto" }}>
    <table>
      <thead>
        <tr>
          <th>Invoice #</th>
          <th>Customer</th>
          <th>Phone</th>
          <th>Beat</th>
          <th>Salesman</th>
          <th>Invoice Date</th>
          <th style={{ textAlign: "right" }}>Days</th>
          <th style={{ textAlign: "right" }}>Invoice Amt</th>
          <th style={{ textAlign: "right" }}>Balance</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <EmptyRow colSpan={9} title="No pending bills" />
        ) : (
          rows.map((row) => (
            <tr key={row.invoiceId}>
              <td>{row.invoiceNumber}</td>
              <td title={row.customerName}>{row.customerName}</td>
              <td>{row.customerPhone || "-"}</td>
              <td>{row.beatName || "-"}</td>
              <td>{row.salesmanName || "-"}</td>
              <td>{fmtDate(row.invoiceDate)}</td>
              <td style={{ textAlign: "right" }}>{row.overdueDays}</td>
              <td style={{ textAlign: "right" }}>{money(row.invoiceAmount)}</td>
              <td style={{ textAlign: "right" }}>{money(row.balanceAmount)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const SHEETS = {
  trip: {
    path: "trip-sheet",
    file: "TripSheet",
    title: "Print Trip Sheet",
    hint: "New bills and old pending bills of the load sheet with their values (gross, scheme, discounts, tax, credit notes, returns, net payable).",
  },
  loading: {
    path: "loading-sheet",
    file: "LoadingSheet",
    title: "Print Loading Sheet",
    hint: "Products of all the bills on the load sheet: MRP, sales and returns / cancellation quantities in CS / DZ / EA, total quantity.",
  },
  unloading: {
    path: "unloading-sheet",
    file: "UnloadingSheet",
    title: "Print Unloading Sheet",
    hint: "Returned goods by bill and by warehouse, full and part returns / cancellations, new and old bills, and the delivery boy's collections with denominations.",
  },
};

const loadSheetLabel = (option) =>
  `${option.loadSheetNumber || "No load sheet no."} — ${option.loadingDate ? fmtDate(option.loadingDate) : "no date"} (${option.bills} bill${
    option.bills === 1 ? "" : "s"
  })`;

/**
 * Picks the delivery boy, load sheet number and loading date for a sheet. The delivery boy's load sheets are listed newest
 * first and the latest is picked by default; the number and date can still be changed by hand.
 */
const PrintSheetDialog = ({ kind, deliveryPersons, initialDeliveryUserId, onClose }) => {
  const sheet = SHEETS[kind];
  const [deliveryUserId, setDeliveryUserId] = useState(initialDeliveryUserId || "");
  const [options, setOptions] = useState([]);
  const [loadSheetNumber, setLoadSheetNumber] = useState("");
  const [loadingDate, setLoadingDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOptions([]);
    setLoadSheetNumber("");
    setLoadingDate("");
    if (!deliveryUserId) return undefined;
    api
      .get(`${RECEIVABLES_API}/delivery-assignments/load-sheets`, { deliveryUserId })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setOptions(list);
        if (list.length) {
          setLoadSheetNumber(list[0].loadSheetNumber || "");
          setLoadingDate(list[0].loadingDate || "");
        }
      })
      .catch((error) => toast.error(error.message || "Could not load the load sheets"));
    return () => {
      cancelled = true;
    };
  }, [deliveryUserId]);

  const pick = (index) => {
    const option = options[Number(index)];
    if (!option) return;
    setLoadSheetNumber(option.loadSheetNumber || "");
    setLoadingDate(option.loadingDate || "");
  };
  const pickedIndex = options.findIndex((o) => (o.loadSheetNumber || "") === loadSheetNumber && (o.loadingDate || "") === loadingDate);

  // checks the picks; null when something is missing (the error is shown)
  const sheetParams = () => {
    if (!deliveryUserId) {
      toast.error("Select a delivery boy");
      return null;
    }
    if (!loadSheetNumber.trim() && !loadingDate) {
      toast.error("Select a load sheet number or a loading date");
      return null;
    }
    const params = { deliveryUserId };
    if (loadSheetNumber.trim()) params.loadSheetNumber = loadSheetNumber.trim();
    if (loadingDate) params.loadingDate = loadingDate;
    return params;
  };

  // the sheet in a new window with the browser's print dialog, as the collections print
  const printPage = async () => {
    const params = sheetParams();
    if (!params) return;
    setBusy(true);
    try {
      await printSheet(kind, () =>
        api.get(`${RECEIVABLES_API}/delivery-assignments/${sheet.path}`, kind === "loading" ? { ...params, print: true } : params)
      );
      onClose();
    } catch (error) {
      console.error(`${sheet.title} print failed`, error);
      toast.error(error.message || "Could not print the sheet");
    } finally {
      setBusy(false);
    }
  };

  const print = async () => {
    const params = sheetParams();
    if (!params) return;
    setBusy(true);
    try {
      const suffix = loadSheetNumber.trim() || loadingDate;
      await api.downloadFile(`${RECEIVABLES_API}/delivery-assignments/${sheet.path}/pdf`, params, `${sheet.file}_${suffix}.pdf`);
      onClose();
    } catch (error) {
      console.error(`${sheet.title} failed`, error);
      toast.error(error.message || "Could not print the sheet");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 520 }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">{sheet.title}</h3>
          <button className="close-btn" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="page-subtitle" style={{ marginBottom: 12 }}>
          {sheet.hint}
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <label style={{ display: "grid", gap: 4 }}>
            <span>Delivery boy</span>
            <select value={deliveryUserId} onChange={(e) => setDeliveryUserId(e.target.value)}>
              <option value="">-- Select delivery boy --</option>
              {deliveryPersons.map((p) => (
                <option key={p.userId} value={p.userId}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>Load sheet (latest first)</span>
            <select value={pickedIndex >= 0 ? pickedIndex : ""} onChange={(e) => pick(e.target.value)} disabled={!options.length}>
              {pickedIndex < 0 ? <option value="">{options.length ? "-- Custom --" : "No load sheets for this delivery boy"}</option> : null}
              {options.map((option, index) => (
                <option key={`${option.loadSheetNumber}-${option.loadingDate}`} value={index}>
                  {loadSheetLabel(option)}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "grid", gap: 4 }}>
              <span>Loading sheet no.</span>
              <input type="text" value={loadSheetNumber} onChange={(e) => setLoadSheetNumber(e.target.value)} maxLength={50} placeholder="Any" />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span>Loading date</span>
              <input type="date" value={loadingDate} onChange={(e) => setLoadingDate(e.target.value)} />
            </label>
          </div>
        </div>
        <div className="flex gap-2" style={{ justifyContent: "flex-end", marginTop: 16 }}>
          <button className="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="secondary" onClick={print} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Download size={14} /> {busy ? "Preparing..." : "Download PDF"}
          </button>
          <button className="primary" onClick={printPage} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Printer size={14} /> {busy ? "Preparing..." : "Print"}
          </button>
        </div>
      </div>
    </div>
  );
};

const toggleIn = (setter) => (id) =>
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

/** Hand invoices to a delivery user and take them back (legacy delivery_inv_assignment.aspx). */
const DeliveryInvoiceAssignment = () => {
  const { salesmen, beats, deliveryPersons } = useReceivableLookups(["salesmen", "beats", "deliveryPersons"]);
  const settings = useReceivableSettings();
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [salesmanIds, setSalesmanIds] = useState([]);
  const [beatIds, setBeatIds] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [selectedUnassigned, setSelectedUnassigned] = useState(new Set());
  const [deliveryUserId, setDeliveryUserId] = useState("");
  const [loadSheetNumber, setLoadSheetNumber] = useState("");
  const [assigned, setAssigned] = useState(EMPTY_ASSIGNED);
  const [loadingDate, setLoadingDate] = useState(todayIso());
  // which sheet's print dialog is open: "trip", "loading", "unloading" or null
  const [printing, setPrinting] = useState(null);
  // load sheet numbers already generated for the selected delivery person in the date range (suggestions)
  const [loadSheetNumbers, setLoadSheetNumbers] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState(new Set());
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);

  const beatOptions = beats
    .filter((b) => salesmanIds.length === 0 || salesmanIds.includes(b.salesmanUserId))
    .map((b) => ({ value: b.id, label: b.name }));

  const loadUnassigned = async () => {
    if (fromDate > toDate) {
      toast.error("From date cannot be after to date");
      return;
    }
    const data = await api.get(`${RECEIVABLES_API}/delivery-assignments/unassigned`, {
      fromDate,
      toDate,
      salesmanUserIds: idList(salesmanIds),
      beatIds: idList(beatIds),
    });
    setUnassigned(Array.isArray(data) ? data : []);
    setSelectedUnassigned(new Set());
  };

  const loadAssigned = async (userId = deliveryUserId) => {
    if (!userId) {
      setAssigned(EMPTY_ASSIGNED);
      setLoadSheetNumbers([]);
      return;
    }
    const [data, numbers] = await Promise.all([
      api.get(`${RECEIVABLES_API}/delivery-assignments/assigned`, { deliveryUserId: userId, fromDate, toDate }),
      api.get(`${RECEIVABLES_API}/delivery-assignments/load-sheet-numbers`, { deliveryUserId: userId, fromDate, toDate }),
    ]);
    setAssigned({ ...EMPTY_ASSIGNED, ...(data || {}) });
    setLoadSheetNumbers(Array.isArray(numbers) ? numbers : []);
    setSelectedAssigned(new Set());
  };

  const generateLoadSheetNumber = () =>
    run(async () => {
      if (!deliveryUserId) throw new Error("Select a delivery person");
      const result = await api.post(`${RECEIVABLES_API}/delivery-assignments/load-sheet-numbers`, null, { deliveryUserId });
      setLoadSheetNumber(result.loadSheetNumber);
      setLoadSheetNumbers((prev) => [result.loadSheetNumber, ...prev]);
      toast.success(`Load sheet number ${result.loadSheetNumber} generated`);
    });

  const run = async (work) => {
    setLoading(true);
    try {
      await work();
    } catch (error) {
      console.error("Delivery assignment request failed", error);
      toast.error(error.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run(() => loadAssigned(deliveryUserId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryUserId]);

  const assign = () =>
    run(async () => {
      if (!deliveryUserId) throw new Error("Select a delivery person");
      if (selectedUnassigned.size === 0) throw new Error("Select at least one invoice");
      if (settings?.loadSheetNumberRequired && !loadSheetNumber.trim()) throw new Error("Enter the load sheet number");
      if (!loadingDate) throw new Error("Enter the loading date");
      const result = await api.post(`${RECEIVABLES_API}/delivery-assignments`, {
        deliveryUserId: Number(deliveryUserId),
        invoiceIds: Array.from(selectedUnassigned),
        loadSheetNumber: loadSheetNumber.trim() || null,
        loadingDate,
      });
      toast.success(`${result.assigned} invoice(s) assigned`);
      setWarning(
        result.customersWithoutPhone?.length
          ? `Delivery OTP could not be sent — please add phone numbers for: ${result.customersWithoutPhone.join(", ")}`
          : ""
      );
      setLoadSheetNumber("");
      await loadUnassigned();
      await loadAssigned();
    });

  const unassign = () =>
    run(async () => {
      if (selectedAssigned.size === 0) throw new Error("Select at least one invoice to unassign");
      const result = await api.post(`${RECEIVABLES_API}/delivery-assignments/unassign`, { ids: Array.from(selectedAssigned) });
      toast.success(`${result.unassigned} invoice(s) unassigned`);
      await loadAssigned();
      if (unassigned.length || selectedUnassigned.size) await loadUnassigned();
    });

  const selectedUnassignedRows = unassigned.filter((row) => selectedUnassigned.has(row.invoiceId));
  const assignedRows = assigned.rows.map((row) => ({ ...row.invoice, _assignment: row }));
  const extraColumns = [
    { label: "Assigned At", render: (row) => fmtDateTime(row._assignment.assignedAt) },
    { label: "Load Sheet", render: (row) => row._assignment.loadSheetNumber || "-" },
    { label: "Loading Date", render: (row) => fmtDate(row._assignment.loadingDate) },
    { label: "Pending Bills", render: (row) => (row._assignment.pendingBills > 0 ? row._assignment.pendingBills : "Nil") },
    ...(assigned.otpVisible ? [{ label: "Delivery OTP", render: (row) => row._assignment.deliveryOtp || "-" }] : []),
  ];

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="filters-bar">
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <MultiSelect
          options={salesmen.map((s) => ({ value: s.userId, label: s.name }))}
          selected={salesmanIds}
          onChange={(ids) => {
            setSalesmanIds(ids);
            setBeatIds([]);
          }}
          placeholder="All salesmen"
        />
        <MultiSelect options={beatOptions} selected={beatIds} onChange={setBeatIds} placeholder="All beats" />
        <button className="primary" onClick={() => run(async () => {
          await loadUnassigned();
          await loadAssigned();
        })}>
          Generate
        </button>
      </div>

      <h3 style={{ margin: "18px 0 0", fontWeight: 600 }}>Unassigned invoices ({unassigned.length})</h3>
      <InvoiceTable
        rows={unassigned}
        selected={selectedUnassigned}
        onToggle={toggleIn(setSelectedUnassigned)}
        onToggleAll={(all) => setSelectedUnassigned(all ? new Set(unassigned.map((r) => r.invoiceId)) : new Set())}
      />
      <div className="page-subtitle" style={{ marginTop: 8 }}>
        Selected: {selectedUnassignedRows.length} bill(s), balance ₹{money(sumBy(selectedUnassignedRows, (r) => r.balanceAmount))}
      </div>

      <div className="filters-bar" style={{ marginTop: 16 }}>
        <select value={deliveryUserId} onChange={(e) => setDeliveryUserId(e.target.value)} aria-label="Delivery person">
          <option value="">-- Select delivery person --</option>
          {deliveryPersons.map((p) => (
            <option key={p.userId} value={p.userId}>
              {p.name}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={loadSheetNumber}
          onChange={(e) => setLoadSheetNumber(e.target.value)}
          placeholder={settings?.loadSheetNumberRequired ? "Load sheet no (required)" : "Load sheet no (optional)"}
          maxLength={50}
          list="delivery-load-sheet-numbers"
        />
        <datalist id="delivery-load-sheet-numbers">
          {loadSheetNumbers.map((number) => (
            <option key={number} value={number} />
          ))}
        </datalist>
        <button className="secondary" onClick={generateLoadSheetNumber} disabled={!deliveryUserId} title="Generate the next load sheet number">
          Generate No.
        </button>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }} title="Day the goods are loaded for this delivery">
          Loading date
          <input type="date" value={loadingDate} onChange={(e) => setLoadingDate(e.target.value)} aria-label="Loading date" />
        </label>
        <button className="primary" onClick={assign}>
          Assign to delivery
        </button>
        <button className="secondary" onClick={unassign} disabled={selectedAssigned.size === 0}>
          Unassign selected
        </button>
        <button className="secondary" onClick={() => setPrinting("trip")} title="Bills of a load sheet with their values">
          <Printer size={14} /> Print Trip Sheet
        </button>
        <button className="secondary" onClick={() => setPrinting("loading")} title="Products to load for a load sheet">
          <Printer size={14} /> Print Loading Sheet
        </button>
        <button className="secondary" onClick={() => setPrinting("unloading")} title="Returned goods and collections of a load sheet">
          <Printer size={14} /> Print Unloading Sheet
        </button>
      </div>
      {warning ? <div className="field-error" style={{ fontSize: 13, marginTop: 8 }}>{warning}</div> : null}

      <h3 style={{ margin: "18px 0 0", fontWeight: 600 }}>
        New bills assigned to {deliveryPersons.find((p) => String(p.userId) === String(deliveryUserId))?.name || "—"} ({assigned.rows.length}
        {" "}bills, balance ₹{money(assigned.totalBalance)})
      </h3>
      <InvoiceTable
        rows={assignedRows}
        selected={selectedAssigned}
        onToggle={toggleIn(setSelectedAssigned)}
        onToggleAll={(all) => setSelectedAssigned(all ? new Set(assignedRows.map((r) => r.invoiceId)) : new Set())}
        extraColumns={extraColumns}
      />

      <h3 style={{ margin: "18px 0 0", fontWeight: 600 }}>
        Pending bills of these customers ({assigned.pendingRows.length} bills, balance ₹{money(assigned.totalPendingBalance)})
      </h3>
      <div className="page-subtitle" style={{ marginTop: 4 }}>
        Older bills with an outstanding balance for the customers of the new bills, on the same beats. They print on the trip sheet as
        Old Bills.
      </div>
      <PendingBillsTable rows={assigned.pendingRows} />

      {printing ? (
        <PrintSheetDialog
          kind={printing}
          deliveryPersons={deliveryPersons}
          initialDeliveryUserId={deliveryUserId}
          onClose={() => setPrinting(null)}
        />
      ) : null}
    </div>
  );
};

export default DeliveryInvoiceAssignment;
